import { orderBy, where } from "firebase/firestore";
import { useEffect, useMemo, useRef } from "react";
import {
    Accordion,
    Alert,
    Button,
    Card,
    Col,
    ListGroup,
    Row,
} from "react-bootstrap";
import {
    FaChevronRight,
    FaExternalLinkAlt,
    FaFlask,
    FaPlus,
} from "react-icons/fa";
import { TbSettingsExclamation } from "react-icons/tb";
import { JSONTree } from "react-json-tree";
import { Link, useSearchParams } from "react-router";

import { getLocationsForApp } from "../../api/api.jsx";
import { getFirebaseConsoleUrl } from "../../api/firestoreBaseApi.jsx";
import {
    AppDocument,
    useAppCollection,
    useLocationCollectionGroup,
    useOrganizationCollection,
} from "../../api/firestoreWrappers.generated.jsx";
import { AppDependencyTree } from "../../components/AppDependencyTree/index.jsx";
import AppListItem from "../../components/AppListItem/index.jsx";
import Layout, { Title } from "../../components/Layout/Layout.jsx";
import SearchQueryInput from "../../components/SearchQueryInput/index.jsx";
import urls from "../../urls.jsx";

function AppDependencyTreeView({
    appDependencyTreeData,
    appNames,
    handleSelectFromTree,
}) {
    const containerRef = useRef(null);

    useEffect(() => {
        if (!appDependencyTreeData) return;
        if (
            (!appDependencyTreeData.children ||
                appDependencyTreeData.children.length === 0) &&
            (!appDependencyTreeData.parents ||
                appDependencyTreeData.parents.length === 0)
        ) {
            return;
        }

        // Clear previous render
        containerRef.current.innerHTML = "";

        // Build SVG
        const svg = AppDependencyTree(appDependencyTreeData, {
            label: (d) => appNames?.[d.id] ?? d.id,
            width: 900,
            padding: 100,
            link: handleSelectFromTree
                ? (data) => handleSelectFromTree(data.id)
                : null,
        });

        // Mount SVG
        containerRef.current.appendChild(svg);
    }, [appDependencyTreeData, appNames, handleSelectFromTree]);

    return <div ref={containerRef} className="mb2" />;
}

function buildAdjacency(apps) {
    const parentsMap = {};
    const childrenMap = {};

    for (const { id, dependencies = [] } of apps) {
        parentsMap[id] = dependencies;
        childrenMap[id] ??= [];

        for (const dep of dependencies) {
            parentsMap[dep] ??= [];
            childrenMap[dep] ??= [];
            childrenMap[dep].push(id);
        }
    }

    return { parentsMap, childrenMap };
}

function buildTree(map, rootId, key, arrow, visited = new Set()) {
    const node = { id: rootId, [key]: [] };

    for (const nextId of map[rootId] ?? []) {
        const edge = `${rootId}${arrow}${nextId}`;
        if (visited.has(edge)) continue;
        visited.add(edge);
        node[key].push(buildTree(map, nextId, key, arrow, visited));
    }

    return node;
}

const buildDescendantTree = (childrenMap, rootId, visited) =>
    buildTree(childrenMap, rootId, "children", "->", visited);

const buildAncestorTree = (parentsMap, rootId, visited) =>
    buildTree(parentsMap, rootId, "parents", "<-", visited);

function buildFocusFamily(parentsMap, childrenMap, focusId) {
    return {
        id: focusId,
        parents: (parentsMap[focusId] ?? []).map((id) =>
            buildAncestorTree(parentsMap, id, new Set()),
        ),
        children: (childrenMap[focusId] ?? []).map((id) =>
            buildDescendantTree(childrenMap, id, new Set()),
        ),
    };
}

function getAllOffspring(childrenMap, rootId) {
    const result = new Set();
    const stack = [...(childrenMap[rootId] ?? [])];

    while (stack.length > 0) {
        const current = stack.pop();
        if (result.has(current)) continue;

        result.add(current);
        stack.push(...(childrenMap[current] ?? []));
    }

    return [...result];
}

function buildOffspringMap(childrenMap) {
    const offspringMap = {};

    for (const appId of Object.keys(childrenMap)) {
        offspringMap[appId] = getAllOffspring(childrenMap, appId);
    }

    return offspringMap;
}

function groupByOrgLocation(entries) {
    const result = {};

    for (const { location, sourceAppId } of entries) {
        const { organizationId, id: locationId } = location;

        result[organizationId] ??= {};
        result[organizationId][locationId] ??= new Set();
        result[organizationId][locationId].add(sourceAppId);
    }

    // Convert Sets → arrays
    for (const orgId in result) {
        for (const locId in result[orgId]) {
            result[orgId][locId] = [...result[orgId][locId]];
        }
    }

    return result;
}

function collectOffspringLocations(appId, offspringMap, appToLocations) {
    const appIds = offspringMap[appId] ?? [];
    const entries = [];

    for (const sourceAppId of appIds) {
        const locations = appToLocations[sourceAppId] ?? [];
        for (const location of locations) {
            entries.push({
                location,
                sourceAppId,
            });
        }
    }

    return entries;
}

export default function AdminAppListPage() {
    const [apps] = useAppCollection([orderBy("name", "asc")]);
    const [organizations] = useOrganizationCollection();
    const [locations] = useLocationCollectionGroup([
        where("deleted", "==", false),
    ]);
    const [searchParams, setSearchParams] = useSearchParams();
    const itemRefs = useRef(new Map());

    const selectedAppId = searchParams.get("appId");
    const appNameQuery = searchParams.get("q") ?? "";
    const filteredApps = apps.filter((app) => {
        return (
            appNameQuery === "" ||
            app.name.toLowerCase().includes(appNameQuery.toLowerCase())
        );
    });

    const updateSearchParams = (updater) => {
        const next = new URLSearchParams(searchParams);
        updater(next);
        setSearchParams(next, { replace: true });
    };

    const isPartiallyInViewport = (el) => {
        const rect = el.getBoundingClientRect();
        const viewHeight =
            window.innerHeight || document.documentElement.clientHeight;

        return rect.top < viewHeight && rect.bottom > 0;
    };

    const registerScrollItemRef = (appId) => (el) => {
        if (!el) return;
        itemRefs.current.set(appId, el);

        if (appId === selectedAppId) {
            setTimeout(() => {
                if (!isPartiallyInViewport(el) && appNameQuery === "") {
                    el.scrollIntoView({ block: "start" });
                }
            }, 400);
        }
    };

    const organizationNames = Object.fromEntries(
        organizations.map((org) => [org.id, org.name]),
    );
    const locationNames = Object.fromEntries(
        locations.map((loc) => [loc.id, loc.name]),
    );
    const appNames = Object.fromEntries(apps.map((app) => [app.id, app.name]));

    const { parentsMap, childrenMap, offspringMap } = useMemo(() => {
        const { parentsMap, childrenMap } = buildAdjacency(apps);
        const offspringMap = buildOffspringMap(childrenMap);

        return { parentsMap, childrenMap, offspringMap };
    }, [apps]);

    const appToLocations = useMemo(() => {
        const map = {};

        for (const app of apps ?? []) {
            map[app.id] = getLocationsForApp(app.id, locations);
        }

        return map;
    }, [apps, locations]);

    const groupedOffspringLocations = useMemo(() => {
        const result = {};

        for (const appId in appToLocations ?? {}) {
            const entries = collectOffspringLocations(
                appId,
                offspringMap,
                appToLocations,
            );

            result[appId] = groupByOrgLocation(entries);
        }

        return result;
    }, [appToLocations, offspringMap]);

    const groupedAppLocations = useMemo(() => {
        const result = {};

        for (const appId in appToLocations ?? {}) {
            const locations = appToLocations[appId] ?? [];
            const byOrg = {};

            for (const location of locations) {
                const organizationId = location.organizationId;

                if (!byOrg[organizationId]) {
                    byOrg[organizationId] = [];
                }

                byOrg[organizationId].push(location);
            }

            result[appId] = byOrg;
        }

        return result;
    }, [appToLocations]);

    function OrgLocListGroup({ organizationId, locationData }) {
        return (
            <div className="mb-2">
                <div className="small fw-semibold border-bottom">
                    {organizationNames[organizationId]}
                </div>
                <ListGroup variant="flush">
                    {locationData.map(
                        ({
                            locationId,
                            to,
                            hasCustomAppSettings,
                            locationAppSettings,
                            sourceAppIds,
                        }) => {
                            return (
                                <div key={locationId} className="border-bottom">
                                    <ListGroup.Item
                                        action
                                        as={Link}
                                        to={to}
                                        className={`small text-muted p-0 border-0`}
                                    >
                                        <div
                                            className={`ms-4 d-flex justify-content-between align-items-center ${hasCustomAppSettings ? "fw-bold" : ""}`}
                                        >
                                            <div>
                                                {locationNames[locationId]}
                                                {hasCustomAppSettings && (
                                                    <TbSettingsExclamation
                                                        className="ms-1"
                                                        size="16px"
                                                        style={{
                                                            marginTop: "-4px",
                                                        }}
                                                    />
                                                )}
                                                {sourceAppIds && (
                                                    <span className="ms-1 text-secondary fst-italic">
                                                        (
                                                        {sourceAppIds
                                                            .map(
                                                                (id) =>
                                                                    appNames?.[
                                                                        id
                                                                    ] ?? id,
                                                            )
                                                            .join(", ")}
                                                        )
                                                    </span>
                                                )}
                                            </div>
                                            <div>
                                                <FaChevronRight
                                                    style={{
                                                        color: "#b9b9b9",
                                                        marginTop: "-4px",
                                                    }}
                                                />
                                            </div>
                                        </div>
                                    </ListGroup.Item>
                                    {hasCustomAppSettings && (
                                        <div className="ms-4 mb-3 fw-bold">
                                            <JSONTree
                                                data={locationAppSettings}
                                                hideRoot={true}
                                                sortObjectKeys={true}
                                                shouldExpandNodeInitially={() =>
                                                    true
                                                }
                                                invertTheme={true}
                                                theme={{
                                                    extend: "monokai",
                                                    tree: {
                                                        marginTop: "0px",
                                                        marginBottom: "0px",
                                                        fontSize: "14px",
                                                        lineHeight: "1",
                                                    },
                                                    base0D: "#212529", // Text color
                                                    base00: "transparent", // Background
                                                }}
                                            />
                                        </div>
                                    )}
                                </div>
                            );
                        },
                    )}
                </ListGroup>
            </div>
        );
    }

    function AppItemSection({ title, children, className }) {
        return (
            <div className={className}>
                <div className="small text-uppercase text-secondary fw-bold border-bottom border-3 mb-1">
                    {title}
                </div>
                {children}
            </div>
        );
    }

    return (
        <Layout>
            <title>All apps | MyCorvus</title>
            {/* Make new breadcrumb when merging */}
            <Card className="mb-4">
                <Title title="Apps">
                    <Button
                        variant="outline-success"
                        as={Link}
                        to={urls.appCreate()}
                    >
                        <FaPlus className="me-2" />
                        New App
                    </Button>
                </Title>
            </Card>

            <Row className="mb-3">
                <Col>
                    <SearchQueryInput
                        defaultValue={appNameQuery}
                        onValueChange={(val) => {
                            updateSearchParams((params) => {
                                if (val) {
                                    params.set("q", val);
                                } else {
                                    params.delete("appId");
                                    params.delete("q");
                                }
                            });
                        }}
                        placeholder={"Search apps"}
                    />
                </Col>
            </Row>

            <Card className="mb-4">
                <Accordion
                    activeKey={selectedAppId}
                    onSelect={(key) => {
                        updateSearchParams((params) => {
                            if (key) {
                                params.set("appId", key);
                            } else {
                                params.delete("appId");
                            }
                        });
                    }}
                >
                    {filteredApps.length === 0 ? (
                        <Alert variant="warning" className="m-0">
                            <FaFlask className="me-2" />{" "}
                            <span className="fst-italic">No apps found</span>
                        </Alert>
                    ) : (
                        filteredApps.map((app) => {
                            const appDocumentPath = AppDocument(app.id).path;
                            const appDependencyTree = buildFocusFamily(
                                parentsMap,
                                childrenMap,
                                app.id,
                            );
                            const appLocations =
                                appToLocations?.[app?.id] ?? {};
                            const appOrganizationIds = Array.from(
                                new Set(
                                    appLocations.map((l) => l.organizationId),
                                ),
                            );
                            const installedLocations =
                                groupedAppLocations?.[app.id] ?? {};
                            const dependencyLocations =
                                groupedOffspringLocations?.[app.id] ?? {};

                            return (
                                <Accordion.Item
                                    key={app?.id}
                                    eventKey={app.id}
                                    ref={registerScrollItemRef(app.id)}
                                >
                                    <Accordion.Header>
                                        <AppListItem
                                            app={app}
                                            badgeText={
                                                <>
                                                    {appOrganizationIds.length}{" "}
                                                    {appOrganizationIds.length ===
                                                    1
                                                        ? "org"
                                                        : "orgs"}
                                                    , {appLocations.length}{" "}
                                                    {appLocations.length === 1
                                                        ? "loc"
                                                        : "locs"}
                                                </>
                                            }
                                            appSettingsUrl={urls.appSettings(
                                                app?.id,
                                            )}
                                        />
                                    </Accordion.Header>
                                    <Accordion.Body>
                                        {(appDependencyTree?.children?.length >
                                            0 ||
                                            appDependencyTree?.parents?.length >
                                                0) && (
                                            <AppItemSection
                                                title={"App dependencies"}
                                            >
                                                <AppDependencyTreeView
                                                    appDependencyTreeData={
                                                        appDependencyTree
                                                    }
                                                    appNames={appNames}
                                                    handleSelectFromTree={(
                                                        appId,
                                                    ) => {
                                                        updateSearchParams(
                                                            (params) => {
                                                                params.set(
                                                                    "appId",
                                                                    appId,
                                                                );
                                                            },
                                                        );
                                                    }}
                                                />
                                            </AppItemSection>
                                        )}
                                        {Object.keys(app?.settings ?? {})
                                            .length > 0 && (
                                            <AppItemSection
                                                title={
                                                    <a
                                                        href={getFirebaseConsoleUrl(
                                                            appDocumentPath,
                                                        )}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        style={{
                                                            textDecoration:
                                                                "none",
                                                            color: "inherit",
                                                        }}
                                                    >
                                                        App settings
                                                        <FaExternalLinkAlt
                                                            className="ms-2"
                                                            style={{
                                                                marginTop:
                                                                    "-4px",
                                                            }}
                                                        />
                                                    </a>
                                                }
                                            >
                                                <JSONTree
                                                    data={app.settings}
                                                    hideRoot={true}
                                                    sortObjectKeys={true}
                                                    shouldExpandNodeInitially={() =>
                                                        true
                                                    }
                                                    invertTheme={true}
                                                    theme={{
                                                        extend: "monokai",
                                                        tree: {
                                                            marginTop: "0px",
                                                            marginBottom: "0px",
                                                            fontSize: "14px",
                                                            lineHeight: "1",
                                                        },
                                                        base0D: "#212529", // Text color
                                                        base00: "transparent", // Background
                                                    }}
                                                />
                                            </AppItemSection>
                                        )}

                                        {Object.keys(installedLocations)
                                            .length === 0 &&
                                            Object.keys(dependencyLocations)
                                                .length === 0 && (
                                                <AppItemSection
                                                    title="This app is not installed in any location"
                                                    className="mt-4"
                                                />
                                            )}

                                        {Object.keys(installedLocations)
                                            .length > 0 && (
                                            <AppItemSection
                                                title="Installed directly in"
                                                className="mt-4"
                                            >
                                                {Object.entries(
                                                    installedLocations,
                                                ).map(
                                                    ([
                                                        organizationId,
                                                        locations,
                                                    ]) => {
                                                        const locationData =
                                                            locations.map(
                                                                (location) => {
                                                                    const locationAppSettings =
                                                                        location?.apps?.find(
                                                                            (
                                                                                locationApp,
                                                                            ) =>
                                                                                locationApp.id ===
                                                                                app?.id,
                                                                        )?.settings;
                                                                    const hasCustomAppSettings =
                                                                        Object.keys(
                                                                            locationAppSettings ??
                                                                                {},
                                                                        )
                                                                            .length >
                                                                        0;

                                                                    return {
                                                                        locationId:
                                                                            location.id,
                                                                        hasCustomAppSettings,
                                                                        locationAppSettings,
                                                                        to:
                                                                            urls.locationApps(
                                                                                organizationId,
                                                                                location.id,
                                                                            ) +
                                                                            `?appId=${app?.id}`,
                                                                    };
                                                                },
                                                            );

                                                        return (
                                                            <OrgLocListGroup
                                                                key={
                                                                    organizationId
                                                                }
                                                                organizationId={
                                                                    organizationId
                                                                }
                                                                locationData={
                                                                    locationData
                                                                }
                                                            />
                                                        );
                                                    },
                                                )}
                                            </AppItemSection>
                                        )}
                                        {Object.keys(dependencyLocations)
                                            .length > 0 && (
                                            <AppItemSection
                                                title="Installed as dependency in"
                                                className="mt-4"
                                            >
                                                {Object.entries(
                                                    dependencyLocations,
                                                ).map(
                                                    ([
                                                        organizationId,
                                                        locations,
                                                    ]) => {
                                                        const locationData =
                                                            Object.entries(
                                                                locations,
                                                            ).map(
                                                                ([
                                                                    locationId,
                                                                    sourceAppIds,
                                                                ]) => {
                                                                    return {
                                                                        locationId,
                                                                        to: urls.locationApps(
                                                                            organizationId,
                                                                            locationId,
                                                                        ),
                                                                        sourceAppIds,
                                                                    };
                                                                },
                                                            );

                                                        return (
                                                            <OrgLocListGroup
                                                                key={
                                                                    organizationId
                                                                }
                                                                organizationId={
                                                                    organizationId
                                                                }
                                                                locationData={
                                                                    locationData
                                                                }
                                                            />
                                                        );
                                                    },
                                                )}
                                            </AppItemSection>
                                        )}
                                    </Accordion.Body>
                                </Accordion.Item>
                            );
                        })
                    )}
                </Accordion>
            </Card>
        </Layout>
    );
}
